import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ApplicationLog } from '../../../../services/log.service';

export interface LogDetailDialogData {
  log: ApplicationLog;
}

@Component({
  selector: 'app-log-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSnackBarModule,
  ],
  templateUrl: './log-detail-dialog.component.html',
  styleUrls: ['./log-detail-dialog.component.scss'],
})
export class LogDetailDialogComponent {
  dialogRef = inject<MatDialogRef<LogDetailDialogComponent>>(MatDialogRef);
  data = inject<LogDetailDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);

  isCopySuccess = false;
  isExceptionCopySuccess = false;

  getLevelClass(level: string): string {
    switch (level?.toLowerCase()) {
      case 'error':
      case 'fatal':
        return 'level-error';
      case 'warning':
        return 'level-warning';
      case 'information':
        return 'level-info';
      default:
        return 'level-debug';
    }
  }

  formatTimestamp(timestamp: string): string {
    // Backend sends DateTimeOffset with timezone information
    const date = new Date(timestamp);
    const formatted = date.toLocaleString();
    // Extract short timezone abbreviation (e.g., EST, PST)
    const timezone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    return `${formatted} (${timezone})`;
  }

  copyMessage(): void {
    navigator.clipboard.writeText(this.data.log.message).then(
      () => {
        this.showCopySuccess();
      },
      () => {
        this.snackBar.open('Failed to copy message', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      }
    );
  }

  private showCopySuccess(): void {
    this.isCopySuccess = true;
    setTimeout(() => {
      this.isCopySuccess = false;
    }, 2000);
  }

  copyException(): void {
    if (!this.data.log.exception) return;
    navigator.clipboard.writeText(this.data.log.exception).then(
      () => {
        this.showExceptionCopySuccess();
      },
      () => {
        this.snackBar.open('Failed to copy exception', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      }
    );
  }

  private showExceptionCopySuccess(): void {
    this.isExceptionCopySuccess = true;
    setTimeout(() => {
      this.isExceptionCopySuccess = false;
    }, 2000);
  }

  copyToClipboard(): void {
    const log = this.data.log;
    const content = `Level: ${log.level}
Timestamp: ${this.formatTimestamp(log.timeStamp)}
Request Path: ${log.requestPath || 'N/A'}
User ID: ${log.userId || 'N/A'}
Employee ID: ${log.employeeId || 'N/A'}

Message:
${log.message}${log.exception ? `

Exception:
${log.exception}` : ''}`;

    navigator.clipboard.writeText(content).then(
      () => {
        this.snackBar.open('Log details copied to clipboard', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      () => {
        this.snackBar.open('Failed to copy to clipboard', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      }
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
